import { useState, useEffect } from "react";
import { useWatch, useFormContext, useFormState } from "react-hook-form";
import styles from "./index.module.scss";
import CommentGroup from "../../../ADP/CommentGroup";
import TextInput from "../../../TextInput";
import CheckboxGroup from "../../../CheckboxGroup";
import RadioGroup from "../../../RadioGroup";
import PageMarker from "../../../ADP/PageMarker";
import Tooltip from "../../../Tooltip";
import Modal from "../../../Modal";
import TextLink from "../../../TextLinks";
import { Text } from "../../../Type";
import Button from "../../../Button";
import {
  dateNotInFutureFieldOptions,
  healthcardNumberFieldOptions,
  healthcardVersionCodeFieldOptions,
  nonSpecialCharacterOptions,
  phoneExtensionFieldOptions,
  phoneNumberFieldOptions,
  postalCodeFieldOptions,
} from "../../../../utils/forms/validation";
import NotificationBanner from "../../../NotificationBanner";
import {
  typeOfBenefitOptions,
  benefitsOptions,
  coverageOptions,
} from "../../../../utils/labelMap/LabelMap";
import { useClientFileContext } from "../../../../contexts/ClientFileProvider";
import { useStoreContext } from "../../../../contexts/StoreProvider";

// #region options
const sexOptions = [
  { label: "Female", value: "f" },
  { label: "Male", value: "m" },
];

const cepOptions = [
  {
    label: "Yes",
    value: "y-cep",
  },
  { label: "No", value: "n-cep" },
];

// #endregion

const onlyNumbers = {
  value: /^[0-9]*$/,
  message: "Please only enter numbers.",
};
// #endregion

const Section1 = () => {
  const { state: { externalUrls }, } = useStoreContext();
  const { clientFile, formMode: mode } = useClientFileContext();
  const { trigger, setValue, getValues, clearErrors } = useFormContext();

  // #region Logic
  const fileStatus = clientFile.status;
  const benefitsObject = useWatch({ name: "section1.benefits" });
  const inputStreetName = useWatch({ name: "section1.street-name" });
  const checkBenefits = benefitsObject === "y-benefits";

  // Clear errors for benefits fields when they are hidden
  useEffect(() => {
    if (!checkBenefits) {
      clearErrors(["section1.type-of-benefit", "section1.benefits-ID"]);
    }
  }, [checkBenefits, clearErrors]);

  // Handle dual-field validation
  // ToDo: Address performance impact (lag when typing in street name field)
  const { touchedFields } = useFormState();
  useEffect(() => {
    if (touchedFields?.section1?.["lot-concession-rural"]) {
      trigger("section1.lot-concession-rural");
    }
  }, [touchedFields, inputStreetName, trigger]);
  // Put the validation message on lot since there is more space
  const lotRequired = !inputStreetName;

  const [prevSex, setPrevSex] = useState(useWatch({ name: "section1.sex" }));
  const currSex = useWatch({ name: "section1.sex" });
  useEffect(() => {
    if (currSex !== prevSex) {
      if (prevSex && currSex?.length === 2) {
        setValue(
          "section1.sex",
          currSex.filter((x) => !prevSex.includes(x))
        );
      }
      setPrevSex(currSex);
    }
  }, [currSex, prevSex, setValue]);

  const [openCoverageModal, setOpenCoverageModal] = useState(false);
  const [prevCoverage, setPrevCoverage] = useState(
    getValues("section1.coverage")
  );
  const currCoverage = useWatch({ name: "section1.coverage" });
  useEffect(() => {
    // TODO: Removed isDirty for now due to initial page load not registering popup
    if (currCoverage !== prevCoverage) {
      if (currCoverage?.length > 0) {
        setOpenCoverageModal(true);
      }
    }
    setPrevCoverage(currCoverage);
  }, [currCoverage, prevCoverage]);

  return (
    <>
      <PageMarker page={1} total={13} />
      <CommentGroup
        section="section1"
        id="1-personal-info"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <NotificationBanner
          warning
          content="Please ensure all information entered in this section exactly matches what is registered with the Ontario Government, as seen on the client's health card."
          className={styles.matchNotice}
        />
        <div className={styles.gridThirds}>
          <TextInput
            section="section1"
            id="last-name"
            label="Last Name"
            placeholder="Last Name"
            fieldOptions={{
              ...nonSpecialCharacterOptions,
              required: "This is a required field.",
            }}
            maxLength={40}
            readOnly={mode !== "edit"}
          />
          <TextInput
            section="section1"
            id="first-name"
            label="First Name"
            placeholder="First Name"
            fieldOptions={{
              ...nonSpecialCharacterOptions,
              required: "This is a required field.",
            }}
            maxLength={40}
            readOnly={mode !== "edit"}
          />
          <TextInput
            section="section1"
            id="middle-initial"
            label="Middle Initial"
            placeholder="Initial"
            fieldOptions={{
              ...nonSpecialCharacterOptions,
            }}
            maxLength={40}
            readOnly={mode !== "edit"}
            tooltip={
              <Tooltip
                id="middle-initial-tooltip"
                content="This must match the client's health card exactly, and may be one or more characters"
              />
            }
          />
        </div>
        <div className={styles.grid4030}>
          <TextInput
            section="section1"
            id="health-card"
            label="Health Card Number"
            placeholder="0000-000-000"
            requiredText="This is a required field."
            fieldOptions={{
              ...healthcardNumberFieldOptions,
              required: "This is a required field.",
            }}
            readOnly={mode !== "edit"}
          />
          <TextInput
            section="section1"
            id="version-code"
            label="Version Code"
            placeholder="A or AA"
            fieldOptions={healthcardVersionCodeFieldOptions}
            readOnly={mode !== "edit"}
          />
        </div>
        <div className={styles.gridThirds}>
          <TextInput
            section="section1"
            id="dob"
            label="Date of Birth"
            placeholder="YYYY/MM/DD"
            fieldOptions={{
              ...dateNotInFutureFieldOptions,
              required: "This is a required field.",
            }}
            readOnly={mode !== "edit"}
          />
        </div>
        <CheckboxGroup
          section="section1"
          name="sex"
          label="Sex"
          options={sexOptions}
          horizontal
          requiredText="This is a required field."
          readOnly={mode !== "edit"}
        />
      </CommentGroup>

      <CommentGroup
        section="section1"
        id="2-LTCH"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <TextInput
          section="section1"
          id="LTCH"
          label="Name of Long-Term Care Home (LTCH)"
          placeholder="Long-Term Care Home"
          fieldOptions={nonSpecialCharacterOptions}
          maxLength={50}
          readOnly={mode !== "edit"}
        />
      </CommentGroup>
      <CommentGroup
        section="section1"
        id="2-address"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <Text size="large" weight="bold" className={styles.addressHeading}>
          Address{" "}
          <span className={styles.addressTooltipWrapper}>
            <Tooltip
              id="unit-tooltip"
              content="Please enter a full street address -or- a Lot/Concession/Rural Route"
            />
          </span>
        </Text>
        <div className={styles.gridThirds}>
          <TextInput
            section="section1"
            id="unit-number"
            label="Unit Number"
            placeholder=""
            fieldOptions={nonSpecialCharacterOptions}
            maxLength={6}
            readOnly={mode !== "edit"}
          />
          <TextInput
            section="section1"
            id="street-number"
            label="Street Number"
            placeholder=""
            fieldOptions={nonSpecialCharacterOptions}
            maxLength={9}
            readOnly={mode !== "edit"}
          />
          <TextInput
            section="section1"
            id="street-name"
            label="Street Name"
            placeholder=""
            fieldOptions={nonSpecialCharacterOptions}
            maxLength={100}
            readOnly={mode !== "edit"}
          />
        </div>
        <TextInput
          section="section1"
          id="lot-concession-rural"
          label="Lot/Concession/Rural Route"
          placeholder=""
          fieldOptions={{
            ...nonSpecialCharacterOptions,
            required: lotRequired
              ? "Please enter either a Street address (above) or a Lot/Concession/Rural Route."
              : undefined,
          }}
          maxLength={100}
          readOnly={mode !== "edit"}
        />
        <div className={styles.grid401525}>
          <TextInput
            section="section1"
            id="city"
            label="City"
            placeholder="City"
            fieldOptions={{
              ...nonSpecialCharacterOptions,
              required: "This is a required field.",
            }}
            maxLength={40}
            readOnly={mode !== "edit"}
          />
          <TextInput
            section="section1"
            id="province"
            label="Province"
            placeholder="ON"
            disabled
            readOnly={mode !== "edit"}
          />
          <TextInput
            section="section1"
            id="postal-code"
            label="Postal Code"
            placeholder="A1A1A1"
            fieldOptions={{
              ...postalCodeFieldOptions,
              required: "This is a required field.",
            }}
            readOnly={mode !== "edit"}
          />
        </div>
        <div className={styles.grid401525}>
          <TextInput
            section="section1"
            id="home-tel"
            label="Home Telephone Number"
            placeholder="000-000-0000"
            fieldOptions={phoneNumberFieldOptions}
            readOnly={mode !== "edit"}
          />
        </div>
        <div className={[styles.grid4025, styles.businessPhoneRow].join(" ")}>
          <TextInput
            section="section1"
            id="business-tel"
            label="Business Telephone Number"
            placeholder="000-000-0000"
            fieldOptions={phoneNumberFieldOptions}
            readOnly={mode !== "edit"}
          />
          <TextInput
            section="section1"
            id="extension"
            label="Extension"
            placeholder="000"
            fieldOptions={phoneExtensionFieldOptions}
            readOnly={mode !== "edit"}
          />
        </div>
      </CommentGroup>
      <CommentGroup
        section="section1"
        id="3-cep-client"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <RadioGroup
          section="section1"
          name="cep"
          label="Client is a CEP Client"
          options={cepOptions}
          horizontal
          requiredText="This is a required field."
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="cep-client-tooltip"
              content="The Central Equipment Pool (CEP) for High Technology Wheelchairs provides ADP Clients throughout Ontario with new and recycled high technology wheelchairs (those with power tilt and/or power wheelchairs as a component). For more information please see the ADP manual section 5."
              links={[
                {
                  id: "link-1",
                  text: "ADP Manual",
                  href: externalUrls.mobility_devices_policy_and_administration_manual,
                },
              ]}
            />
          }
        />
      </CommentGroup>
      <CommentGroup
        section="section1"
        id="4-social-benefits"
        className={styles.margin}
        commentBoxPosition={!checkBenefits ? "top" : "bottom"}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <RadioGroup
          section="section1"
          name="benefits"
          label="Client is receiving social assistance benefits"
          options={benefitsOptions}
          horizontal
          requiredText="This is a required field."
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="social-assistance-benefits-tooltip"
              content="Please answer if the client is receiving social assistance benefits. If yes, please specify which ones. "
            />
          }
        />
        {checkBenefits && (
          <RadioGroup
            section="section1"
            name="type-of-benefit"
            description="Please check one:"
            options={typeOfBenefitOptions}
            requiredText="You selected that the client is receiving social assistance benefits, so please select a program below."
            readOnly={mode !== "edit"}
          />
        )}
        {checkBenefits && (
          <TextInput
            section="section1"
            id="benefits-ID"
            label="Please enter social assistance benefits ID"
            placeholder="Social Assistance Benefits ID number"
            fieldOptions={{
              pattern: onlyNumbers,
            }}
            readOnly={mode !== "edit"}
          />
        )}
      </CommentGroup>
      <CommentGroup
        section="section1"
        id="5-coverage-mobility-devices"
        className={styles.margin}
        commentBoxPosition="top"
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <CheckboxGroup
          section="section1"
          name="coverage"
          options={coverageOptions}
          label="Client is eligible to receive coverage for Mobility Devices from:"
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="eligibility-tooltip"
              content="Please answer if you are eligible to receive coverage for mobility devices from Workplace Safety & Insurance Board (WSIB) and/or Veteran Affairs Canada (VAC)."
            />
          }
        />
      </CommentGroup>
      {fileStatus !== "Completed" && (
        <Modal
          isOpen={openCoverageModal}
          title="Coverage for Mobility Devices"
          onClose={() => setOpenCoverageModal(false)}
        >
          <div className={styles.modalText}>
            ADP does not fund clients who are on WSIB or VAC for the same
            disability. Please check the
            <TextLink
              to={externalUrls.mobility_devices_product_manual}
              externalURL
            >
              ADP Manual
            </TextLink>
            for further information
          </div>
          <div className={styles.horizontalLine} />
          <div className={styles.modalButton}>
            <Button primary onClick={() => setOpenCoverageModal(false)}>
              Close and return to form
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Section1;
